import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Collaborator } from '../../types/api';

interface CollaboratorRowProps {
  collaborator: Collaborator;
  index: number;
  showIndex: boolean;
  onPersonClick: (personId: number, e: React.MouseEvent) => void;
  onProjectClick: (projectId: number, e: React.MouseEvent) => void;
}

export default function CollaboratorRow({
  collaborator,
  index,
  showIndex,
  onPersonClick,
  onProjectClick,
}: CollaboratorRowProps) {
  const { t } = useTranslation('person');
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(o => !o);

  return (
    <li className="group">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        className="flex w-full cursor-pointer items-center gap-3 py-2.5 text-left transition-colors hover:bg-muted/40 -mx-2 px-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {showIndex && (
          <span className="w-5 shrink-0 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
            {index + 1}
          </span>
        )}
        <span className="flex-1 min-w-0 truncate text-sm font-medium text-ctu-text">
          {collaborator.name}
        </span>
        {collaborator.faculty_abbr && (
          <Badge
            variant="outline"
            className="shrink-0 font-normal text-[10px] tracking-wide text-ctu-text-muted"
          >
            {collaborator.faculty_abbr}
          </Badge>
        )}
        <span className="w-8 shrink-0 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
          {collaborator.shared_count}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-ctu-text-muted transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </div>

      {open && (
        <div className="mb-2 ml-7 mr-1 mt-1 rounded-md border border-border bg-muted/20">
          <div className="flex items-center justify-end border-b border-border px-3 py-2">
            <a
              href={`/person/${collaborator.id}`}
              onClick={e => {
                e.stopPropagation();
                onPersonClick(collaborator.id, e);
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded text-xs font-medium text-primary no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('buttons.viewProfile')}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <ul className="flex flex-col divide-y divide-border list-none p-0">
          {collaborator.projects.map(project => (
            <li
              key={project.id}
              className="flex items-start gap-3 px-3 py-2"
            >
              <span className="flex-1 min-w-0 whitespace-normal break-words text-sm text-ctu-text">
                {project.name}
              </span>
              {typeof project.year === 'number' && (
                <span className="w-12 shrink-0 pt-0.5 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
                  {project.year}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-ctu-text-muted hover:text-primary"
                onClick={e => {
                  e.stopPropagation();
                  onProjectClick(project.id, e);
                }}
                title={t('buttons.viewDetails')}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
          </ul>
        </div>
      )}
    </li>
  );
}
